import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Button } from '@swyftx/aviary/atoms/Button';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Tooltip } from '@swyftx/aviary/atoms/Tooltip/Tooltip';
import { Body } from '@swyftx/aviary/atoms/Typography';
import { PortfolioTransfer, TransferFunds } from '@swyftx/aviary/icons/outlined';
import { Menu, MenuItem } from '@swyftx/aviary/molecules/Menu';

import { Modals } from '@global-components/Modals/Modals.enum';
import { useModal } from '@global-components/Modals/useModal.hooks';

import { AppStore, UserStore } from '@shared/store';

import { useCountryAsset } from '@hooks/Assets/useCountryAsset';
import { useAvo } from '@hooks/Avo/useAvo';
import { MarginAccountTransferModal } from '@routes/SwyftxMargin/components/Account';

import { ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { AppFeature, useIsFeatureEnabled } from 'src/config';
import { useMarginAccounts } from 'src/lib/margin/hooks/account';
import { useSwyftxMargin } from 'src/lib/margin/hooks/useSwyftxMargin';
import { useNavigateRoute } from 'src/lib/navigation/hooks';
import { NavigationURLs } from 'src/lib/navigation/types';

const LOG_TAG = 'APP_HEADER';

const AppHeaderTransferButton: React.FC = observer(() => {
  const { isFeatureEnabled } = useIsFeatureEnabled();
  const countryAsset = useCountryAsset();
  const { pathname } = useLocation();
  const { openModal } = useModal();
  const { navigate } = useNavigateRoute();
  const { swyftxMarginEnabled, isUnderMaintenance } = useSwyftxMargin();
  const { data, isFetching } = useMarginAccounts();

  const { isKyc1Complete, userProfile } = UserStore.useUserStore;
  const { isDemo } = AppStore.useAppStore;
  const isKyc1RequiredForCryptoTransactions = userProfile?.metadata?.cryptoRequiresKyc1 === true;
  const portfolioTransferEnabled = isFeatureEnabled(AppFeature.PortfolioTransfer);
  const avo = useAvo();
  const { t: marginT } = useTranslation('margin');
  const { t } = useTranslation('profile');

  const handleDepositClicked = () => {
    avo.depositFundsTapped({
      screen: pathname,
      component: LOG_TAG,
    });
    openModal(Modals.DepositReceive, { selectedAsset: countryAsset });
  };

  const handleWithdrawalClicked = () => {
    avo.withdrawFundsTapped({
      screen: pathname,
      component: LOG_TAG,
    });
    openModal(Modals.WithdrawSend, { selectedAsset: countryAsset });
  };

  const handleOpenAssetMigrator = () => {
    navigate(NavigationURLs.PortfolioTransfer);
  };

  if (isDemo) {
    return (
      <Tooltip title={t('transferFunds.demoMode')}>
        <FlexLayout className='cursor-pointer rounded-8 p-6'>
          <Button disabled={isDemo} size='md'>
            {t('transferFunds.title')}
          </Button>
        </FlexLayout>
      </Tooltip>
    );
  }

  return (
    <Menu
      triggerContent={
        <FlexLayout className='cursor-pointer rounded-8 px-6'>
          <Button size='md'>{t('transferFunds.title')}</Button>
        </FlexLayout>
      }
    >
      <MenuItem
        action={handleDepositClicked}
        text={t('transferFunds.deposit.title')}
        description={t('transferFunds.deposit.description', { currency: countryAsset?.code })}
        icon={<ArrowDownLeft />}
      />
      <MenuItem
        action={handleWithdrawalClicked}
        text={t('transferFunds.withdraw.title')}
        description={t('transferFunds.withdraw.description', { currency: countryAsset?.code })}
        icon={<ArrowUpRight />}
      />
      {!!data?.accounts.length && !isFetching && swyftxMarginEnabled && data.accounts?.[0].active && (
        <MarginAccountTransferModal>
          <span>
            <MenuItem
              disabled={isUnderMaintenance}
              tooltip={isUnderMaintenance ? marginT('maintenance.tooltips.transfer') : ''}
              text={t('transferFunds.internalTransfer.title')}
              description={t('transferFunds.internalTransfer.description')}
              icon={<TransferFunds />}
            />
          </span>
        </MarginAccountTransferModal>
      )}
      {portfolioTransferEnabled && (
        <MenuItem
          action={handleOpenAssetMigrator}
          text={t('transferFunds.transferPortfolio.title')}
          description={
            <Body
              size='xsmall'
              color={isKyc1RequiredForCryptoTransactions && !isKyc1Complete() ? 'error' : 'secondary'}
            >
              {isKyc1RequiredForCryptoTransactions && !isKyc1Complete()
                ? t('transferFunds.transferPortfolio.verification')
                : t('transferFunds.transferPortfolio.description')}
            </Body>
          }
          icon={<PortfolioTransfer />}
        />
      )}
    </Menu>
  );
});

export { AppHeaderTransferButton };
